/**
 * src/shared/constants/permissions.js
 *
 * Client-side mirror of the backend Permission enum and ROLE_PERMISSIONS matrix
 * from app/domain/admin/base_service.py.
 *
 * Keep in sync with the backend when roles or permissions change.
 * Used by usePermissions() to derive what the current admin can do from
 * their role string — avoiding a backend change to add permissions[] to /me.
 */

export const PERMISSIONS = {
  // User management
  VIEW_USERS:    'view_users',
  EDIT_USERS:    'edit_users',
  DELETE_USERS:  'delete_users',
  DISABLE_USERS: 'disable_users',

  // Order management
  VIEW_ORDERS:   'view_orders',
  EDIT_ORDERS:   'edit_orders',
  CANCEL_ORDERS: 'cancel_orders',
  REFUND_ORDERS: 'refund_orders',

  // Product management
  VIEW_PRODUCTS:    'view_products',
  CREATE_PRODUCTS:  'create_products',
  EDIT_PRODUCTS:    'edit_products',
  DELETE_PRODUCTS:  'delete_products',
  BULK_UPLOAD:      'bulk_upload',

  // Payment management
  VIEW_PAYMENTS:        'view_payments',
  FORCE_REFUND:         'force_refund',
  RECONCILE_PAYMENTS:   'reconcile_payments',

  // Shipping
  VIEW_SHIPMENTS:   'view_shipments',
  MANAGE_SHIPMENTS: 'manage_shipments',
  CREATE_SHIPMENTS: 'create_shipments',
  UPDATE_SHIPMENTS: 'update_shipments',
  GENERATE_LABELS:  'generate_labels',

  // Analytics
  VIEW_ANALYTICS: 'view_analytics',
  EXPORT_DATA:    'export_data',

  // System
  VIEW_LOGS:              'view_logs',
  MANAGE_ADMINS:          'manage_admins',
  SYSTEM_SETTINGS:        'system_settings',
  VIEW_CUSTOM_REQUESTS:   'view_custom_requests',
  MANAGE_CUSTOM_REQUESTS: 'manage_custom_requests',
  VIEW_SYSTEM_HEALTH:     'view_system_health',
  VIEW_SYSTEM_LOGS:       'view_system_logs',
  EXPORT_LOGS:            'export_logs',
  MANAGE_BACKUPS:         'manage_backups',
  MANAGE_CONFIG:          'manage_config',
  MANAGE_ADMIN_USERS:     'manage_admin_users',
  MANAGE_CACHE:           'manage_cache',
  MANAGE_COUPONS:         'manage_coupons',
};

// Mirror of ROLE_PERMISSIONS in backend base_service.py
// super_admin is handled separately (all permissions).
export const ROLE_PERMISSIONS = {
  admin: [
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.EDIT_USERS,
    PERMISSIONS.DISABLE_USERS,
    PERMISSIONS.VIEW_ORDERS,
    PERMISSIONS.EDIT_ORDERS,
    PERMISSIONS.CANCEL_ORDERS,
    PERMISSIONS.REFUND_ORDERS,
    PERMISSIONS.VIEW_PRODUCTS,
    PERMISSIONS.CREATE_PRODUCTS,
    PERMISSIONS.EDIT_PRODUCTS,
    PERMISSIONS.DELETE_PRODUCTS,
    PERMISSIONS.BULK_UPLOAD,
    PERMISSIONS.VIEW_PAYMENTS,
    PERMISSIONS.FORCE_REFUND,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.EXPORT_DATA,
    PERMISSIONS.VIEW_LOGS,
    PERMISSIONS.VIEW_SHIPMENTS,
    PERMISSIONS.CREATE_SHIPMENTS,
    PERMISSIONS.UPDATE_SHIPMENTS,
    PERMISSIONS.GENERATE_LABELS,
    PERMISSIONS.VIEW_CUSTOM_REQUESTS,
    PERMISSIONS.MANAGE_CUSTOM_REQUESTS,
    PERMISSIONS.SYSTEM_SETTINGS,
    PERMISSIONS.VIEW_SYSTEM_HEALTH,
    PERMISSIONS.VIEW_SYSTEM_LOGS,
    PERMISSIONS.EXPORT_LOGS,
    PERMISSIONS.MANAGE_BACKUPS,
    PERMISSIONS.MANAGE_CACHE,
    PERMISSIONS.MANAGE_COUPONS,
  ],
  manager: [
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.EDIT_USERS,
    PERMISSIONS.VIEW_ORDERS,
    PERMISSIONS.EDIT_ORDERS,
    PERMISSIONS.CANCEL_ORDERS,
    PERMISSIONS.VIEW_PRODUCTS,
    PERMISSIONS.CREATE_PRODUCTS,
    PERMISSIONS.EDIT_PRODUCTS,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.EXPORT_DATA,
    PERMISSIONS.VIEW_CUSTOM_REQUESTS,
    PERMISSIONS.MANAGE_CUSTOM_REQUESTS,
    PERMISSIONS.MANAGE_COUPONS,
  ],
  viewer: [
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.VIEW_ORDERS,
    PERMISSIONS.VIEW_PRODUCTS,
    PERMISSIONS.VIEW_PAYMENTS,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_CUSTOM_REQUESTS,
    PERMISSIONS.VIEW_SHIPMENTS,
  ],
  analyst: [
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_ORDERS,
    PERMISSIONS.VIEW_PRODUCTS,
    PERMISSIONS.EXPORT_DATA,
  ],
};